import { preview } from './preview.js';
import { useEffect, useSyncExternalStore } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import {
  scopeKey,
  type ClientMessage,
  type CustomFieldDto,
  type EventDto,
  type MemberDto,
  type MeDto,
  type ProjectSnapshotDto,
  type Scope,
  type ServerMessage,
  type TaskDto,
  type TaskListDto,
  type CommentDto,
  type CommentPageDto,
  type AttachmentDto,
  type TaskStatusDto,
  type SectionDto,
  type LabelDto,
  type ChecklistItemDto,
  type FileDto,
  type PageCommentDto,
  type AreaDto,
  type ChatMessageDto,
  type ChatPageDto,
  type PostDto,
  type TalkStateDto,
  type TopicDetailDto,
  type TopicDto,
  type TopicListItemDto,
  type PageDetailDto,
  type PageDto,
  type FolderDto,
  type FolderContentDto,
  type MyDayDto,
  type MyTaskDto,
  type TaskPageDto,
  type WorkspaceDto,
} from '@werft/contracts';
import { keys, queryClient, type Snapshot } from './queries.js';
import { presence } from './presence.js';
import { withPendingProject } from './projectPending.js';

type Status = 'connecting' | 'online' | 'offline';

/** Gemeinsames Schreiben (P12): eine Sitzung je offener Seite (features/docs/collab.ts) */
export type CollabMessage = Extract<ServerMessage, { pageId: string }>;
export interface CollabChannel {
  message: (m: CollabMessage) => void;
  open: () => void;
  close: () => void;
}

interface Sub {
  scope: Scope;
  /** zuletzt angewendete seq; Ereignisse bis hierhin werden ignoriert (keine Doppelanwendung) */
  cursor: number;
  refs: number;
}

/**
 * Eine WebSocket-Verbindung je Tab (Leitlinie, Seite 8; Muster aus KaBa). Ströme werden mit dem Cursor ihres
 * Snapshots abonniert; nach einem Abbruch wird mit wachsendem Abstand und Zufallsanteil neu verbunden und ab dem
 * letzten Cursor nachgeladen. Ereignisse patchen den Query-Cache gezielt statt alles neu zu laden.
 */
class Realtime {
  private ws: WebSocket | null = null;
  private subs = new Map<string, Sub>();
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private ping: ReturnType<typeof setInterval> | undefined;
  private active = false;
  status: Status = 'connecting';
  private listeners = new Set<() => void>();
  private revokedListeners = new Set<(scope: Scope) => void>();
  private collab = new Map<string, Set<CollabChannel>>();

  /** Sitzung einer Seite anmelden; ist die Verbindung offen, tritt sie sofort bei */
  attachCollab(pageId: string, ch: CollabChannel): () => void {
    const set = this.collab.get(pageId) ?? new Set<CollabChannel>();
    set.add(ch);
    this.collab.set(pageId, set);
    if (this.ws?.readyState === WebSocket.OPEN) ch.open();
    return () => {
      set.delete(ch);
      if (!set.size && this.collab.get(pageId) === set) this.collab.delete(pageId);
    };
  }

  collabCount(pageId: string): number {
    return this.collab.get(pageId)?.size ?? 0;
  }

  /** false = gerade keine Verbindung */
  sendCollab(msg: ClientMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  isOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  start() {
    if (this.active) return;
    this.active = true;
    this.connect();
    document.addEventListener('visibilitychange', this.wake);
    window.addEventListener('online', this.wake);
  }

  stop() {
    this.active = false;
    clearTimeout(this.timer);
    clearInterval(this.ping);
    document.removeEventListener('visibilitychange', this.wake);
    window.removeEventListener('online', this.wake);
    this.ws?.close();
    this.ws = null;
    this.subs.clear();
  }

  /** Strom abonnieren; `cursor` stammt aus dem Snapshot, der gerade geladen wurde */
  subscribe(scope: Scope, cursor: number): () => void {
    // P21: Vorschau „Als Gast ansehen“ ohne Live-Abgleich (der Strom liefe mit den Rechten der Leitung)
    if (scope.type === 'project' && preview()?.projectId === scope.id) return () => {};
    const key = scopeKey(scope);
    const sub = this.subs.get(key);
    if (sub) {
      sub.refs++;
      if (cursor > sub.cursor) sub.cursor = cursor;
    } else {
      this.subs.set(key, { scope, cursor, refs: 1 });
      this.send({ type: 'subscribe', scope, after: cursor });
    }
    return () => {
      const s = this.subs.get(key);
      if (!s) return;
      if (--s.refs <= 0) {
        this.subs.delete(key);
        this.send({ type: 'unsubscribe', scope });
        if (scope.type === 'project') presence.clear(scope.id);
      }
    };
  }

  /** Präsenz melden (P9): in welchem Projekt ich bin und welche Aufgabe offen ist; null = nirgends */
  setPresence(projectId: string | null, taskId: string | null) {
    if (presence.mine.projectId === projectId && presence.mine.taskId === taskId) return;
    presence.mine = { projectId, taskId };
    this.send({ type: 'presence', projectId, taskId });
  }

  onRevoked(fn: (scope: Scope) => void): () => void {
    this.revokedListeners.add(fn);
    return () => {
      this.revokedListeners.delete(fn);
    };
  }

  subscribeStatus = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
  getStatus = () => this.status;

  private setStatus(s: Status) {
    if (this.status === s) return;
    this.status = s;
    this.listeners.forEach((l) => l());
  }

  private wake = () => {
    if (document.visibilityState === 'visible' && this.active && (!this.ws || this.ws.readyState > WebSocket.OPEN)) {
      clearTimeout(this.timer);
      this.attempt = 0;
      this.connect();
    }
  };

  private send(msg: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private connect() {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/api/ws`);
    this.ws = ws;
    this.setStatus('connecting');
    ws.onopen = () => {
      this.attempt = 0;
      this.setStatus('online');
      for (const s of this.subs.values()) this.send({ type: 'subscribe', scope: s.scope, after: s.cursor });
      clearInterval(this.ping);
      this.ping = setInterval(() => this.send({ type: 'ping' }), 25_000);
      for (const set of this.collab.values()) for (const ch of set) ch.open();
    };
    ws.onmessage = (e) => {
      try {
        this.handle(JSON.parse(String(e.data)) as ServerMessage);
      } catch (err) {
        console.error('Live-Nachricht', err);
      }
    };
    ws.onclose = (e) => {
      clearInterval(this.ping);
      if (this.ws !== ws) return;
      this.ws = null;
      for (const set of this.collab.values()) for (const ch of set) ch.close();
      if (!this.active) return;
      if (e.code === 4401) {
        // Sitzung abgelaufen: Anmeldestatus neu prüfen
        void queryClient.invalidateQueries({ queryKey: keys.me });
        return;
      }
      this.setStatus('offline');
      // 1 s, 2 s, 4 s … höchstens 30 s, mit Zufallsanteil gegen gleichzeitige Wiederverbindungen
      const delay = Math.min(30_000, 1000 * 2 ** this.attempt++) * (0.7 + Math.random() * 0.6);
      this.timer = setTimeout(() => this.connect(), delay);
    };
  }

  private handle(msg: ServerMessage) {
    if (msg.type.startsWith('y-') && 'pageId' in msg) {
      this.collab.get(msg.pageId)?.forEach((ch) => ch.message(msg as CollabMessage));
      return;
    }
    switch (msg.type) {
      case 'event': {
        const sub = this.subs.get(scopeKey(msg.event.scope));
        if (!sub || msg.event.seq <= sub.cursor) return;
        sub.cursor = msg.event.seq;
        applyEvent(msg.event);
        return;
      }
      case 'subscribed': {
        const sub = this.subs.get(scopeKey(msg.scope));
        if (sub && msg.lastSeq > sub.cursor) sub.cursor = msg.lastSeq;
        // Eigene Präsenz erst nach dem Abo melden (der Server nimmt sie nur mit Leserecht an)
        if (msg.scope.type === 'project' && presence.mine.projectId === msg.scope.id) this.send({ type: 'presence', ...presence.mine });
        return;
      }
      case 'presence':
        presence.set(msg.projectId, msg.people);
        return;
      case 'resync':
        // Lücke zu groß: Stand neu laden; das Abo setzt danach mit dem neuen Cursor neu auf
        this.subs.delete(scopeKey(msg.scope));
        void queryClient.invalidateQueries({ queryKey: msg.scope.type === 'workspace' ? keys.workspace(msg.scope.id) : keys.project(msg.scope.id) });
        return;
      case 'revoked':
        this.subs.delete(scopeKey(msg.scope));
        // 0.158 (ADR 0164): laufende Abrufe abbrechen – eine späte Antwort darf entzogene Daten nicht zurückbringen
        void queryClient.cancelQueries({ queryKey: msg.scope.type === 'workspace' ? keys.workspace(msg.scope.id) : keys.project(msg.scope.id) });
        if (msg.scope.type === 'workspace') queryClient.removeQueries({ queryKey: keys.workspace(msg.scope.id) });
        else queryClient.removeQueries({ queryKey: keys.project(msg.scope.id) });
        void queryClient.invalidateQueries({ queryKey: keys.me });
        // R156-06: Zugriff entzogen – Mein Tag liest neu (nichts aus dem entzogenen Projekt bleibt stehen)
        void queryClient.invalidateQueries({ queryKey: keys.myDay });
        this.revokedListeners.forEach((l) => l(msg.scope));
        return;
      case 'inbox':
        void queryClient.invalidateQueries({ queryKey: ['planning'] });
        void queryClient.invalidateQueries({ queryKey: keys.notifications });
        return;
      case 'me-changed':
        for (const queryKey of [['admin'], ['appearance'], keys.config]) void queryClient.invalidateQueries({ queryKey });
        void queryClient.invalidateQueries({ queryKey: keys.me });
        // Projektkarten der Lobby (neue/entzogene Projekte, Umbenennungen)
        void queryClient.invalidateQueries({ queryKey: ['ws'], predicate: (q) => q.queryKey[2] === 'projects' });
        return;
    }
  }
}

/** Ereignis auf den Cache anwenden – dieselben Daten, die auch eine eigene Änderung schreiben würde */
export function applyEvent(e: EventDto) {
  if (e.scope.type === 'project') return applyProjectEvent(e);
  const wsId = e.scope.id;
  switch (e.type) {
    case 'profile.changed': {
      void queryClient.invalidateQueries({ queryKey: keys.members(wsId) });
      return;
    }
    case 'settings.changed': {
      for (const queryKey of [['admin'], ['appearance'], keys.config]) void queryClient.invalidateQueries({ queryKey });
      break;
    }
    case 'workspace.updated': {
      const p = e.payload as { name: string; version: number };
      queryClient.setQueryData<WorkspaceDto>(keys.workspace(wsId), (old) => (old && old.version < p.version ? { ...old, ...p } : old));
      queryClient.setQueryData<MeDto | null>(keys.me, (old) =>
        old ? { ...old, workspaces: old.workspaces.map((w) => (w.id === wsId && w.version < p.version ? { ...w, ...p } : w)) } : old,
      );
      void queryClient.invalidateQueries({ queryKey: keys.adminWorkspaces });
      return;
    }
    case 'member.changed': {
      const m = e.payload as MemberDto;
      queryClient.setQueryData<Snapshot<MemberDto>>(keys.members(wsId), (old) => {
        if (!old) return old;
        const existing = old.items.find((x) => x.userId === m.userId);
        if (existing && existing.version >= m.version) return { ...old, cursor: e.seq };
        const items = existing ? old.items.map((x) => (x.userId === m.userId ? m : x)) : [...old.items, m].sort((a, b) => a.name.localeCompare(b.name, 'de'));
        return { items, cursor: e.seq };
      });
      return;
    }
    case 'member.removed':
      queryClient.setQueryData<Snapshot<MemberDto>>(keys.members(wsId), (old) =>
        old ? { items: old.items.filter((x) => x.userId !== e.entityId), cursor: e.seq } : old,
      );
      return;
    case 'invitation.changed':
      void queryClient.invalidateQueries({ queryKey: keys.invitations(wsId) });
      return;
    case 'project.trashed':
    case 'project.purged':
      // ADR 0125: Lobby (laufend, Archiv, Papierkorb) neu laden
      void queryClient.invalidateQueries({ queryKey: ['ws', wsId] });
      void queryClient.invalidateQueries({ queryKey: ['trash', wsId] });
      return;
  }
}

const pendingViews = new Map<string, ReturnType<typeof setTimeout>>();
function refreshViews(pid: string) {
  if (pendingViews.has(pid)) return;
  pendingViews.set(
    pid,
    setTimeout(() => {
      pendingViews.delete(pid);
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'table'], refetchType: 'active' });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'calendar'], refetchType: 'active' });
      // P26: Abhängigkeiten in offenen Details (Titel, erledigt) mitziehen
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'deps'], refetchType: 'active' });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'taskLinks'], refetchType: 'active' });
    }, 250),
  );
}

function applyProjectEvent(e: EventDto) {
  const pid = e.scope.id;
  if (e.type === 'profile.changed') { void queryClient.invalidateQueries({ queryKey: keys.snapshot(pid) }); return; }
  if (e.type === 'settings.changed') {
    for (const queryKey of [['admin'], ['appearance'], keys.config]) void queryClient.invalidateQueries({ queryKey });
    return;
  }
  // Verlauf ist eine Lesesicht auf das Ereignisprotokoll: offene Verläufe gezielt nachladen
  if (e.type.startsWith('task.') || e.type.startsWith('comment.') || e.type.startsWith('attachment.') || e.type.startsWith('list.') || e.type.startsWith('section.') || e.type.startsWith('file.') || e.type === 'page.changed' || e.type === 'page.removed' || e.type === 'talk.topic.changed' || e.type === 'approval.changed' || e.type.startsWith('area.') || e.type === 'project.updated') {
    void queryClient.invalidateQueries({ queryKey: ['p', pid, 'activity'], refetchType: 'active' });
  }
  // Mein Tag liest projektübergreifend: bei Aufgabenänderungen gezielt neu laden, wenn er gerade offen ist
  if (e.type.startsWith('task.')) void queryClient.invalidateQueries({ queryKey: keys.myDay, refetchType: 'active' });
  // R156-06: Bausteine von Mein Tag (Agenda, Wartet auf mich) bei passenden Änderungen gezielt neu lesen – nur Kennung
  // des Ereignisses zählt, Inhalte liest die geprüfte Abfrage
  if (/^(event|shift|approval|decision)\./.test(e.type) || e.type === 'access.changed' || e.type.startsWith('project.'))
    void queryClient.invalidateQueries({ queryKey: [...keys.myDay, 'blocks'], refetchType: 'active' });
  // Fortschritt im Listenkopf (Zähler im Snapshot): gebündelt nachladen statt bei jedem Ereignis
  if (e.type === 'task.changed' || e.type === 'task.removed') refreshCounts(pid);
  // Tabelle und Kalender (P16) sind Serveransichten (sortiert, gefiltert): gebündelt neu laden, wenn offen
  if (e.type.startsWith('task.') || e.type.startsWith('list.') || e.type.startsWith('status.') || e.type === 'label.removed' || e.type.startsWith('field.')) refreshViews(pid);
  // Etappe C: „seitdem geändert“ an offenen Abnahmen – bei Änderungen an Aufgabe, Notiz oder Datei
  if (e.type === 'task.changed' || e.type === 'page.changed' || e.type.startsWith('file.')) void queryClient.invalidateQueries({ queryKey: ['p', pid, 'approvals'], refetchType: 'active' });
  if (e.type === 'share.changed') void queryClient.invalidateQueries({ queryKey: ['shares', pid] });
  // ADR 0064: Projekteingang – Liste, Vorgang und Formulare gezielt nachladen (nur wenn offen)
  if (e.type.startsWith('intake.')) void queryClient.invalidateQueries({ queryKey: ['p', pid, 'intake'], refetchType: 'active' });
  // ADR 0072: Termine
  if (e.type === 'event.changed' || e.type === 'event.removed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'events'], refetchType: 'active' });
  // ADR 0080: Personen und Anmeldungen – Nutzlast nur Kennungen, gelesen wird über die geprüften Wege
  if (e.type === 'people.changed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'people'], refetchType: 'active' });
  // ADR 0082: Budget & Kosten
  if (e.type === 'resource.changed' || e.type === 'shift.changed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'resources'], refetchType: 'active' });
  if (e.type === 'goal.changed' || e.type === 'risk.changed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'goals'], refetchType: 'active' });
  if (e.type === 'time.changed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'time'], refetchType: 'active' });
  if (e.type === 'budget.changed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'budget'], refetchType: 'active' });
  // ADR 0077: Entscheidungsanfragen – Register, Seitenblatt, nächste Schritte
  if (e.type === 'decision.changed') {
    void queryClient.invalidateQueries({ queryKey: ['p', pid, 'decisions'], refetchType: 'active' });
    void queryClient.invalidateQueries({ queryKey: ['p', pid, 'register'], refetchType: 'active' });
    void queryClient.invalidateQueries({ queryKey: ['p', pid, 'nextsteps'], refetchType: 'active' });
  }
  // ADR 0071: Freigaben-Register (Abnahmen, Entscheidungen in Gesprächen)
  if (e.type === 'approval.run.changed' || e.type === 'approval.changed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'approvalRuns'], refetchType: 'active' });
  if (e.type === 'approval.changed' || e.type === 'talk.topic.changed' || e.type === 'talk.topic.removed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'register'], refetchType: 'active' });
  // ADR 0066: Regeln und Läufe
  if (e.type.startsWith('automation.')) void queryClient.invalidateQueries({ queryKey: ['p', pid, 'automations'], refetchType: 'active' });
  // 0.155 (ADR 0157): Signale der Übersicht rechnet der Server aus den Werkzeugen – nur neu laden, wenn offen
  if (/^(event|shift|resource|budget|time|goal|risk|doc|page|link|people|access|project)\./.test(e.type)) void queryClient.invalidateQueries({ queryKey: ['p', pid, 'signals'], refetchType: 'active' });
  // ADR 0170 (W3): Voraussetzungen der Status und ihr Stand je Aufgabe
  if (e.type === 'status.rule.changed' || e.type === 'field.removed' || e.type === 'field.changed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'statusRules'] });
  if (e.type.startsWith('task.') || e.type === 'approval.changed' || e.type === 'status.rule.changed') void queryClient.invalidateQueries({ queryKey: ['p', pid, 'transitions'], refetchType: 'active' });
  // ADR 0167 (W1): Zustand und Titel an Verweisen folgen dem Inhalt (erledigt, abgesagt, umbenannt) – nur offene Listen
  if (/^(task|event|decision|page|talk|goal|risk|shift|intake)\./.test(e.type)) void queryClient.invalidateQueries({ queryKey: ['p', pid, 'links'], refetchType: 'active' });
  // Projektinsel W12: nächste Schritte rechnet der Server – bei passenden Änderungen gezielt neu laden (nur wenn offen)
  if (e.type.startsWith('task.') || e.type.startsWith('file.') || e.type === 'approval.changed' || e.type.startsWith('talk.') || e.type === 'link.changed' || e.type === 'access.changed')
    void queryClient.invalidateQueries({ queryKey: ['p', pid, 'nextsteps'], refetchType: 'active' });
  switch (e.type) {
    case 'section.changed':
      return upsertSection(pid, e.payload as SectionDto);
    case 'section.removed': {
      const p = e.payload as { id: string };
      removeSection(pid, p.id);
      return;
    }
    case 'label.changed':
      return upsertLabel(pid, e.payload as LabelDto);
    case 'label.removed':
      return removeLabel(pid, e.entityId!);
    case 'field.changed':
      return upsertField(pid, e.payload as CustomFieldDto);
    case 'field.removed':
      return removeField(pid, e.entityId!);
    // P25: geteilte Ansichten – klein, also einfach neu laden
    case 'approval.changed': {
      // Etappe C: Abnahmen des Inhalts und „Braucht Aufmerksamkeit“ neu laden
      const a = e.payload as { kind: string; targetId: string };
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'approvals', a.kind, a.targetId] });
      // W08: Abnahmestand an der Datei in der Ablage
      if (a.kind === 'file') {
        void queryClient.invalidateQueries({ queryKey: ['p', pid, 'files'] });
        void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filequery'] });
      }
      void queryClient.invalidateQueries({ queryKey: keys.overview(pid), refetchType: 'active' });
      return;
    }
    case 'link.changed':
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'links'] });
      // ADR 0116: Kosten an Aufgaben/Aufgaben an Positionen hängen an Verknüpfungen
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'budget'], refetchType: 'active' });
      return;
    case 'page.pinned':
    case 'file.pinned':
    case 'talk.pinned':
      void queryClient.invalidateQueries({ queryKey: keys.pins(pid) });
      void queryClient.invalidateQueries({ queryKey: keys.overview(pid) });
      return;
    case 'tags.changed':
      // 0.152: Tags/geteilte Datei-Ansichten – Begriffe und Zähler neu (Sichtbarkeit entscheidet der Server je Person)
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filetags'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'fileviews'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filequery'] });
      return;
    case 'view.changed':
    case 'view.removed':
      void queryClient.invalidateQueries({ queryKey: keys.views(pid) });
      return;
    case 'file.changed':
      // Datei ↔ Aufgabe (Ablage) zählt auch als Verknüpfung (Audit F10)
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'links'] });
      return upsertFile(pid, e.payload as FileDto);
    case 'file.comment.changed': {
      // W08: Rückmeldungen der Datei und Zähler/„neu“ in der Ablage
      const p = e.payload as { fileId: string };
      void queryClient.invalidateQueries({ queryKey: keys.fileComments(pid, p.fileId) });
      // F02: Anlässe offener Aufgaben (zugeordnete Rückmeldungen) folgen
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'taskfeedback'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'files'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filequery'] });
      return;
    }
    case 'file.removed':
      return removeFile(pid, (e.payload as { id: string }).id);
    case 'folder.changed':
      return upsertFolder(pid, e.payload as FolderDto);
    case 'folder.removed':
      // Inhalt wanderte eine Ebene nach oben: Ablage gezielt neu laden
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'files'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filequery'] });
      return;
    case 'talk.topic.changed':
      return upsertTopic(pid, e.payload as TopicDto);
    case 'talk.topic.removed':
      return removeTopic(pid, (e.payload as { id: string }).id);
    case 'talk.post.changed':
      return upsertPost(pid, e.payload as PostDto);
    case 'talk.post.removed': {
      const p = e.payload as { id: string; topicId: string };
      queryClient.setQueryData<TopicDetailDto>(keys.topic(pid, p.topicId), (old) => (old ? { ...old, posts: old.posts.filter((x) => x.id !== p.id) } : old));
      return;
    }
    case 'talk.message.changed':
      return upsertMessage(pid, e.payload as ChatMessageDto);
    case 'talk.message.removed':
      return removeMessage(pid, (e.payload as { id: string }).id);
    case 'page.changed':
      return upsertPage(pid, e.payload as PageDto);
    case 'page.removed': {
      const p = e.payload as { id: string; ids: string[]; purged?: boolean };
      removePages(pid, p.ids ?? [p.id]);
      void queryClient.invalidateQueries({ queryKey: keys.pageTrash(pid) });
      return;
    }
    case 'page.comment.changed':
      return upsertPageComment(pid, e.payload as PageCommentDto);
    case 'page.comment.removed': {
      const p = e.payload as { id: string; pageId: string };
      queryClient.setQueryData<{ items: PageCommentDto[] }>(keys.pageComments(pid, p.pageId), (old) => (old ? { items: old.items.filter((x) => x.id !== p.id) } : old));
      refreshOpenComments(pid, p.pageId);
      return;
    }
    case 'checklist.changed':
      return upsertChecklistItem(pid, e.payload as ChecklistItemDto);
    case 'checklist.removed': {
      const p = e.payload as { id: string; taskId: string };
      return removeChecklistItem(pid, p.taskId, p.id);
    }
    case 'comment.changed': {
      const c = e.payload as CommentDto;
      return upsertComment(pid, c);
    }
    case 'comment.removed': {
      const p = e.payload as { id: string; taskId: string };
      return removeComment(pid, p.taskId, p.id);
    }
    case 'attachment.changed': {
      const a = e.payload as AttachmentDto;
      // „An Aufgaben“ in der Ablage (Liste und Zähler) folgt den Anhängen
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'fileattachments'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'files'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filequery'] });
      return upsertAttachment(pid, a);
    }
    case 'attachment.removed': {
      const p = e.payload as { id: string; taskId: string };
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'fileattachments'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'files'] });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filequery'] });
      queryClient.setQueryData<{ items: AttachmentDto[] }>(keys.attachments(pid, p.taskId), (old) => (old ? { items: old.items.filter((x) => x.id !== p.id) } : old));
      return;
    }
    case 'task.changed':
      return upsertTask(pid, e.payload as TaskDto);
    case 'task.removed':
      return removeTask(pid, e.entityId!);
    case 'list.changed':
      return upsertList(pid, e.payload as TaskListDto);
    case 'area.changed':
      return upsertArea(pid, e.payload as AreaDto);
    case 'area.removed':
      // Inhalte aller Werkzeuge sind eine Ebene nach oben gewandert: Projekt gezielt neu laden
      void queryClient.invalidateQueries({ queryKey: keys.project(pid) });
      return;
    case 'access.changed':
      // Zugriffsbereich (P15): Sichtbarkeit, Team oder Bereich eines Inhalts geändert – alles dieses Projekts neu
      // laden; Eingang und Mein Tag können Einträge verlieren oder gewinnen
      // ADR 0167 (W1): Verweislisten, die gerade niemand zeigt, nicht mit alten Titeln liegen lassen
      queryClient.removeQueries({ queryKey: ['p', pid, 'links'], type: 'inactive' });
      queryClient.removeQueries({ queryKey: ['p', pid, 'taskLinks'], type: 'inactive' });
      void queryClient.invalidateQueries({ queryKey: keys.project(pid) });
      void queryClient.invalidateQueries({ queryKey: keys.notifications });
      return;
    case 'status.changed':
      return upsertStatus(pid, e.payload as TaskStatusDto);
    case 'board.ordered':
      // ADR 0172: eine Spalte wurde neu verteilt (Altbestand/Kollision) – nur die Aufgaben neu laden
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'tasks'] });
      return;
    case 'list.merged':
    case 'status.removed':
    case 'status.cleared':
      // Viele Aufgaben auf einmal betroffen: Struktur und Aufgaben gezielt neu laden statt Einzelereignisse
      void queryClient.invalidateQueries({ queryKey: keys.project(pid) });
      return;
    case 'project.updated': {
      const p = e.payload as { name: string; color: string | null; archived: boolean; version: number };
      queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) =>
        old && old.project.version < p.version ? { ...old, project: withPendingProject(pid, { ...old.project, ...p }) } : old,
      );
      return;
    }
    case 'project.member.changed':
    case 'project.member.removed':
      // Gästeliste der Leitung (Freigaben, P21) mit nachladen
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'guests'] });
      void queryClient.invalidateQueries({ queryKey: keys.snapshot(pid) });
      return;
    case 'list.removed':
      void queryClient.invalidateQueries({ queryKey: keys.snapshot(pid) });
      return;
  }
}

const byPos = <T extends { position: string; id: string }>(a: T, b: T) => (a.position < b.position ? -1 : a.position > b.position ? 1 : a.id < b.id ? -1 : 1);

/* ---------- Gespräche (P13) ---------- */

/** Wer gerade welchen Teil der Gespräche offen hat (dort gilt Neues sofort als gelesen) */
export const talkViewing = { chat: null as string | null, topic: null as string | null };

export function upsertTopic(pid: string, t: TopicDto) {
  const me = presence.meId;
  for (const state of ['open', 'resolved'] as const) {
    patchLive<{ items: TopicListItemDto[] }>(keys.topics(pid, state), (old) => {
      if (!old) return old;
      const cur = old.items.find((x) => x.id === t.id);
      if (cur && cur.version > t.version) return old;
      const belongs = state === 'open' ? !t.resolvedAt : !!t.resolvedAt;
      const rest = old.items.filter((x) => x.id !== t.id);
      if (!belongs) return { items: rest };
      const newer = !cur || cur.lastActivityAt < t.lastActivityAt;
      const unread = talkViewing.topic === t.id ? false : !cur || newer ? t.lastAuthorId !== me : cur.unread;
      return { items: [{ ...t, unread }, ...rest].sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt)) };
    });
  }
  const detail = queryClient.getQueryData<TopicDetailDto>(keys.topic(pid, t.id));
  // Audit 0.36 F09: neue Entscheidung – Bezüge stehen nur im Thema (mit Sichtprüfung): gezielt nachladen
  if (detail && (t.decision?.id ?? null) !== (detail.decisions?.[0]?.id ?? null)) void queryClient.invalidateQueries({ queryKey: keys.topic(pid, t.id) });
  queryClient.setQueryData<TopicDetailDto>(keys.topic(pid, t.id), (old) => (old && old.topic.version <= t.version ? { ...old, topic: t } : old));
  // „Braucht Aufmerksamkeit“ zählt ausstehende Entscheidungen
  void queryClient.invalidateQueries({ queryKey: keys.overview(pid), refetchType: 'active' });
  // Zähler im Reiter: aus der Liste, sonst neu laden
  const open = queryClient.getQueryData<{ items: TopicListItemDto[] }>(keys.topics(pid, 'open'));
  if (open) patchLive<TalkStateDto>(keys.talk(pid), (old) => (old ? { ...old, topicsUnread: open.items.filter((x) => x.unread).length } : old));
  else void queryClient.invalidateQueries({ queryKey: keys.talk(pid) });
}

export function removeTopic(pid: string, id: string) {
  for (const state of ['open', 'resolved'] as const)
    queryClient.setQueryData<{ items: TopicListItemDto[] }>(keys.topics(pid, state), (old) => (old ? { items: old.items.filter((x) => x.id !== id) } : old));
  queryClient.removeQueries({ queryKey: keys.topic(pid, id) });
  void queryClient.invalidateQueries({ queryKey: keys.talk(pid) });
}

export function upsertPost(pid: string, p: PostDto) {
  queryClient.setQueryData<TopicDetailDto>(keys.topic(pid, p.topicId), (old) => {
    if (!old) return old;
    const cur = old.posts.find((x) => x.id === p.id);
    if (cur && cur.version > p.version) return old;
    return { ...old, posts: cur ? old.posts.map((x) => (x.id === p.id ? p : x)) : [...old.posts, p] };
  });
}

export function upsertMessage(pid: string, m: ChatMessageDto) {
  let isNew = false;
  queryClient.setQueryData<InfiniteData<ChatPageDto>>(keys.chat(pid), (old) => {
    if (!old) return old;
    const pages = old.pages.map((pg) => ({ ...pg, items: pg.items.map((x) => (x.id === m.id && x.version <= m.version ? m : x)) }));
    if (!old.pages.some((pg) => pg.items.some((x) => x.id === m.id))) {
      isNew = true;
      const [first, ...rest] = pages;
      if (first) first.items = [...first.items, m];
      return { ...old, pages: [first!, ...rest] };
    }
    return { ...old, pages };
  });
  if (!queryClient.getQueryData(keys.chat(pid))) isNew = true;
  // Ungelesen nur für Nachrichten anderer, wenn der Chat gerade nicht offen ist
  if (isNew && m.authorId !== presence.meId && talkViewing.chat !== pid) {
    patchLive<TalkStateDto>(keys.talk(pid), (old) => (old && (!old.chatReadAt || old.chatReadAt < m.createdAt) ? { ...old, chatUnread: old.chatUnread + 1 } : old));
  }
}


/**
 * Etappe D (Ursache p13): Ein Live-Ereignis patcht den Cache; läuft für diesen Schlüssel aber gerade eine Abfrage (z. B.
 * kurz nach dem Öffnen), überschreibt deren Antwort mit dem älteren Stand die Änderung. Dann gezielt neu laden.
 */
export function patchLive<T>(key: readonly unknown[], update: (old: T | undefined) => T | undefined) {
  const query = queryClient.getQueryCache().find({ queryKey: key, exact: true });
  if (query?.state.fetchStatus !== 'fetching') return void queryClient.setQueryData<T>(key, update);
  // Eine laufende Erst-Abfrage lässt sich nicht abbrechen (TanStack hängt sich an) – nach ihrem Ende neu laden
  const cache = queryClient.getQueryCache();
  const stop = cache.subscribe((e) => {
    if (e.query !== query || query.state.fetchStatus !== 'idle') return;
    stop();
    void queryClient.invalidateQueries({ queryKey: key, exact: true, refetchType: 'all' });
  });
}

export function removeMessage(pid: string, id: string) {
  queryClient.setQueryData<InfiniteData<ChatPageDto>>(keys.chat(pid), (old) => (old ? { ...old, pages: old.pages.map((pg) => ({ ...pg, items: pg.items.filter((x) => x.id !== id) })) } : old));
}

/* ---------- Notizen (P12) ---------- */

/** Seite in den Seitenbaum, Papierkorb und Seitenkopf übernehmen (eigene Antwort oder Live-Ereignis) */
export function upsertPage(pid: string, p: PageDto) {
  const newer = (old: PageDto | undefined) => !old || old.version < p.version || (old.version === p.version && old.updatedAt < p.updatedAt);
  queryClient.setQueryData<{ items: PageDto[] }>(keys.pages(pid), (old) => {
    if (!old) return old;
    const cur = old.items.find((x) => x.id === p.id);
    if (cur && !newer(cur)) return old;
    const rest = old.items.filter((x) => x.id !== p.id);
    return { items: p.archivedAt ? rest : [...rest, p].sort(byPos) };
  });
  queryClient.setQueryData<PageDetailDto>(keys.page(pid, p.id), (old) => (old && newer(old) ? { ...old, ...p } : old));
  if (!p.archivedAt) queryClient.setQueryData<{ items: PageDto[] }>(keys.pageTrash(pid), (old) => (old ? { items: old.items.filter((x) => x.id !== p.id) } : old));
  // Neue Mitschreibende: Kopf der offenen Seite gezielt nachladen
  const head = queryClient.getQueryData<PageDetailDto>(keys.page(pid, p.id));
  if (head && p.updatedBy && !head.contributors.includes(p.updatedBy)) void queryClient.invalidateQueries({ queryKey: keys.page(pid, p.id), refetchType: 'active' });
}

export function removePages(pid: string, ids: string[]) {
  queryClient.setQueryData<{ items: PageDto[] }>(keys.pages(pid), (old) => (old ? { items: old.items.filter((x) => !ids.includes(x.id)) } : old));
  for (const id of ids) queryClient.setQueryData<PageDetailDto>(keys.page(pid, id), (old) => (old ? { ...old, archivedAt: old.archivedAt ?? new Date().toISOString() } : old));
}

export function upsertPageComment(pid: string, c: PageCommentDto) {
  queryClient.setQueryData<{ items: PageCommentDto[] }>(keys.pageComments(pid, c.pageId), (old) => {
    if (!old) return old;
    const cur = old.items.find((x) => x.id === c.id);
    if (cur && cur.version > c.version) return old;
    return { items: cur ? old.items.map((x) => (x.id === c.id ? c : x)) : [...old.items, c] };
  });
  refreshOpenComments(pid, c.pageId);
}

function refreshOpenComments(pid: string, pageId: string) {
  const items = queryClient.getQueryData<{ items: PageCommentDto[] }>(keys.pageComments(pid, pageId))?.items;
  if (!items) return;
  const open = items.filter((x) => x.id === x.threadId && !x.resolvedAt).length;
  queryClient.setQueryData<PageDetailDto>(keys.page(pid, pageId), (old) => (old ? { ...old, openComments: open } : old));
}

/** Liste in den Projekt-Snapshot einsortieren (eigene Antwort oder Live-Ereignis) */
/** Bereich (P14) in den Snapshot übernehmen (eigene Antwort oder Live-Ereignis) */
export function upsertArea(pid: string, a: AreaDto) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => {
    if (!old) return old;
    const ex = old.areas.find((x) => x.id === a.id);
    if (ex && ex.version > a.version) return old;
    const areas = ex ? old.areas.map((x) => (x.id === a.id ? a : x)) : [...old.areas, a];
    return { ...old, areas: areas.sort(byPos) };
  });
}

export function upsertList(pid: string, l: TaskListDto) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => {
    if (!old) return old;
    const ex = old.lists.find((x) => x.id === l.id);
    if (ex && ex.version >= l.version) return old;
    const lists = ex ? old.lists.map((x) => (x.id === l.id ? l : x)) : [...old.lists, l];
    return { ...old, lists: lists.sort(byPos) };
  });
}

export function upsertStatus(pid: string, s: TaskStatusDto) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => {
    if (!old) return old;
    const ex = old.statuses.find((x) => x.id === s.id);
    if (ex && ex.version >= s.version) return old;
    const statuses = ex ? old.statuses.map((x) => (x.id === s.id ? s : x)) : [...old.statuses, s];
    return { ...old, statuses: statuses.sort(byPos) };
  });
}

const countTimers = new Map<string, ReturnType<typeof setTimeout>>();
/** Zähler offen/erledigt je Liste kommen mit dem Snapshot; nach Aufgabenänderungen gebündelt (3 s) nachladen – seltener neu zeichnen */
export function refreshCounts(pid: string) {
  if (countTimers.has(pid)) return;
  countTimers.set(
    pid,
    setTimeout(() => {
      countTimers.delete(pid);
      void queryClient.invalidateQueries({ queryKey: keys.snapshot(pid), refetchType: 'active' });
      void queryClient.invalidateQueries({ queryKey: ['p', pid, 'overview'], refetchType: 'active' });
    }, 3000),
  );
}

export function upsertSection(pid: string, x: SectionDto) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => {
    if (!old) return old;
    const ex = old.sections.find((s) => s.id === x.id);
    if (ex && ex.version >= x.version) return old;
    const sections = ex ? old.sections.map((s) => (s.id === x.id ? x : s)) : [...old.sections, x];
    return { ...old, sections: sections.sort(byPos) };
  });
}

/** Abschnitt aufgelöst: seine Aufgaben stehen ohne Abschnitt in der Liste (ohne neue Version) */
export function removeSection(pid: string, id: string) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => (old ? { ...old, sections: old.sections.filter((s) => s.id !== id) } : old));
  patchAllTasks(pid, (t) => (t.sectionId === id ? { ...t, sectionId: null } : t));
}

export function upsertLabel(pid: string, x: LabelDto) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => {
    if (!old) return old;
    const ex = old.labels.find((l) => l.id === x.id);
    if (ex && ex.version >= x.version) return old;
    const labels = ex ? old.labels.map((l) => (l.id === x.id ? x : l)) : [...old.labels, x];
    return { ...old, labels: labels.sort((a, b) => a.name.localeCompare(b.name, 'de')) };
  });
}

/** P18: eigenes Feld angelegt/geändert – Reihenfolge nach Position */
export function upsertField(pid: string, x: CustomFieldDto) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => {
    if (!old) return old;
    const ex = old.fields.find((f) => f.id === x.id);
    if (ex && ex.version > x.version) return old;
    const fields = ex ? old.fields.map((f) => (f.id === x.id ? x : f)) : [...old.fields, x];
    // entfernte Auswahlwerte verschwinden auch aus geladenen Aufgaben
    if (ex && x.kind === 'select') {
      const keep = new Set(x.options.map((o) => o.id));
      patchAllTasks(pid, (t) => (x.id in t.fields && !keep.has(String(t.fields[x.id])) ? { ...t, fields: omit(t.fields, x.id) } : t));
    }
    return { ...old, fields: fields.sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : a.id < b.id ? -1 : 1)) };
  });
}

/** Feld gelöscht: Werte verschwinden aus den Aufgaben, die Aufgaben bleiben */
export function removeField(pid: string, id: string) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => (old ? { ...old, fields: old.fields.filter((f) => f.id !== id) } : old));
  patchAllTasks(pid, (t) => (id in t.fields ? { ...t, fields: omit(t.fields, id) } : t));
}

const omit = <T extends Record<string, unknown>>(o: T, k: string): T => {
  const { [k]: _gone, ...rest } = o;
  return rest as T;
};

/** Label gelöscht: Aufgaben verlieren nur die Zuordnung */
export function removeLabel(pid: string, id: string) {
  queryClient.setQueryData<ProjectSnapshotDto>(keys.snapshot(pid), (old) => (old ? { ...old, labels: old.labels.filter((l) => l.id !== id) } : old));
  patchAllTasks(pid, (t) => (t.labelIds.includes(id) ? { ...t, labelIds: t.labelIds.filter((x) => x !== id) } : t));
}

/** Eine Änderung ohne Versionssprung auf alle geladenen Aufgaben anwenden (Seiten, Einzelaufgaben, Unteraufgaben) */
function patchAllTasks(pid: string, fn: (t: TaskDto) => TaskDto) {
  for (const state of ['open', 'done'] as const) {
    queryClient.setQueryData<TaskPages>(keys.tasks(pid, state), (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map(fn) })) } : old));
  }
  queryClient.setQueriesData<TaskDto>({ queryKey: ['p', pid, 'task'] }, (old) => (old ? fn(old) : old));
  queryClient.setQueriesData<{ items: TaskDto[] }>({ queryKey: ['p', pid, 'subtasks'] }, (old) => (old ? { items: old.items.map(fn) } : old));
}

export function upsertChecklistItem(pid: string, c: ChecklistItemDto) {
  queryClient.setQueryData<{ items: ChecklistItemDto[] }>(keys.checklist(pid, c.taskId), (old) => {
    if (!old) return old;
    const ex = old.items.find((x) => x.id === c.id);
    if (ex && ex.version > c.version) return old;
    const items = ex ? old.items.map((x) => (x.id === c.id ? c : x)) : [...old.items, c];
    return { items: items.sort(byPos) };
  });
}

export function removeChecklistItem(pid: string, taskId: string, id: string) {
  queryClient.setQueryData<{ items: ChecklistItemDto[] }>(keys.checklist(pid, taskId), (old) => (old ? { items: old.items.filter((x) => x.id !== id) } : old));
}

/* ---------- Ablage (Paket P11) ---------- */

type FolderContent = FolderContentDto;

/**
 * Datei in die Ablage-Caches einsortieren (eigene Antwort oder Live-Ereignis): in ihrem Ordner ersetzen/ergänzen,
 * aus allen anderen Ordnern entfernen; im Papierkorb, wenn gelöscht. Ältere Versionen überschreiben nie neuere.
 */
export function upsertFile(pid: string, f: FileDto) {
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' });
  for (const [key, old] of queryClient.getQueriesData<FolderContent>({ queryKey: ['p', pid, 'files'] })) {
    if (!old) continue;
    const folderKey = key[3] as string;
    const ex = old.files.find((x) => x.id === f.id);
    if (ex && ex.version > f.version) continue;
    const belongs = !f.deletedAt && (f.folderId ?? 'root') === folderKey;
    const files = old.files.filter((x) => x.id !== f.id);
    queryClient.setQueryData<FolderContent>(key, { ...old, files: belongs ? [...files, f].sort(byName) : files });
  }
  queryClient.setQueryData<{ items: FileDto[] }>(keys.fileTrash(pid), (old) =>
    old ? { items: f.deletedAt ? [f, ...old.items.filter((x) => x.id !== f.id)] : old.items.filter((x) => x.id !== f.id) } : old,
  );
  void queryClient.invalidateQueries({ queryKey: keys.fileVersions(pid, f.id) });
  void queryClient.invalidateQueries({ queryKey: ['p', pid, 'taskfiles'] });
  // Zähler (Papierkorb, Anhänge) kommen mit dem Ordnerinhalt: den offenen Ordner leise nachladen
  void queryClient.invalidateQueries({ queryKey: ['p', pid, 'files'] });
  void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filequery'] });
}

export function removeFile(pid: string, id: string) {
  for (const [key, old] of queryClient.getQueriesData<FolderContent>({ queryKey: ['p', pid, 'files'] })) {
    if (old?.files.some((x) => x.id === id)) queryClient.setQueryData<FolderContent>(key, { ...old, files: old.files.filter((x) => x.id !== id) });
  }
  queryClient.setQueryData<{ items: FileDto[] }>(keys.fileTrash(pid), (old) => (old ? { items: old.items.filter((x) => x.id !== id) } : old));
  void queryClient.invalidateQueries({ queryKey: ['p', pid, 'files'] });
  void queryClient.invalidateQueries({ queryKey: ['p', pid, 'filequery'] });
  void queryClient.invalidateQueries({ queryKey: ['p', pid, 'taskfiles'] });
}

export function upsertFolder(pid: string, f: FolderDto) {
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' });
  for (const [key, old] of queryClient.getQueriesData<FolderContent>({ queryKey: ['p', pid, 'files'] })) {
    if (!old) continue;
    const ex = old.folders.find((x) => x.id === f.id);
    if (ex && ex.version > f.version) continue;
    queryClient.setQueryData<FolderContent>(key, { ...old, folders: [...old.folders.filter((x) => x.id !== f.id), f].sort(byName) });
  }
}

/**
 * Mein Tag ohne Flackern: eine bekannte Aufgabe dort sofort aktualisieren (offen ↔ heute erledigt). Neue Aufgaben
 * kommen über das gezielte Neuladen dazu.
 */
function patchMyDay(t: TaskDto) {
  if (t.id.startsWith('tmp-')) return;
  queryClient.setQueryData<MyDayDto>(keys.myDay, (old) => {
    if (!old) return old;
    const known = [...old.items, ...old.doneToday].find((x) => x.id === t.id);
    if (!known || known.version > t.version) return old;
    const merged: MyTaskDto = { ...known, ...t };
    const items = old.items.filter((x) => x.id !== t.id);
    const doneToday = old.doneToday.filter((x) => x.id !== t.id);
    return merged.completedAt ? { ...old, items, doneToday: [merged, ...doneToday] } : { ...old, items: [...items, merged], doneToday };
  });
}

/** Persönliches Vormerken im Mein-Tag-Stand nachziehen */
export function setMyDayPinned(t: TaskDto & { pinned?: boolean; listName?: string | null }, pinned: boolean) {
  queryClient.setQueryData<MyDayDto>(keys.myDay, (old) => {
    if (!old) return old;
    const known = old.items.find((x) => x.id === t.id);
    if (known) return { ...old, items: old.items.map((x) => (x.id === t.id ? { ...x, pinned } : x)).filter((x) => x.pinned || x.assigneeIds.length > 0) };
    return pinned ? { ...old, items: [...old.items, { ...t, pinned: true, listName: t.listName ?? null }] } : old;
  });
}

type CommentPages = InfiniteData<CommentPageDto, string>;

/** Kommentar einsortieren (eigene Antwort oder Live-Ereignis); Reihenfolge nach Zeit, neueste Seite = pages[0] */
export function upsertComment(pid: string, c: CommentDto) {
  queryClient.setQueryData<CommentPages>(keys.comments(pid, c.taskId), (old) => {
    if (!old) return old;
    const exists = old.pages.some((p) => p.items.some((x) => x.id === c.id));
    if (exists) return { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map((x) => (x.id === c.id ? (x.version > c.version ? x : c) : x)) })) };
    const [first, ...rest] = old.pages;
    if (!first) return old;
    const items = [...first.items, c].sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : 1));
    return { ...old, pages: [{ ...first, items }, ...rest] };
  });
}

export function removeComment(pid: string, taskId: string, id: string) {
  queryClient.setQueryData<CommentPages>(keys.comments(pid, taskId), (old) =>
    old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.filter((x) => x.id !== id) })) } : old,
  );
}

export function upsertAttachment(pid: string, a: AttachmentDto) {
  queryClient.setQueryData<{ items: AttachmentDto[] }>(keys.attachments(pid, a.taskId), (old) =>
    old ? { items: old.items.some((x) => x.id === a.id) ? old.items.map((x) => (x.id === a.id ? a : x)) : [...old.items, a] } : old,
  );
}

type TaskPages = InfiniteData<TaskPageDto, string>;

/**
 * Aufgabe in den Cache einsortieren (eigene Antwort oder Live-Ereignis – derselbe Weg). Ältere Versionen werden
 * nie über neuere geschrieben; erledigte Aufgaben wandern aus der offenen Liste.
 */
export function upsertTask(pid: string, t: TaskDto) {
  patchMyDay(t);
  // Etappe D (a2 einmal rot): eine gerade laufende Abfrage der Details käme mit älterem Stand – dann danach neu laden
  if (!t.id.startsWith('tmp-')) patchLive<TaskDto>(keys.task(pid, t.id), (old) => (old && old.version > t.version ? old : t));
  // Abnahme 0.49 F01: Kalender/Zeitachse lesen eigene Abfragen – bestätigte Stände dort sofort versionsbewusst übernehmen
  // (Folgeaktionen starten sonst auf dem alten Kalenderobjekt). Neu hinzukommende Termine lädt `refreshViews`.
  for (const q of queryClient.getQueryCache().findAll({ queryKey: ['p', pid, 'calendar'] })) {
    const data = q.state.data as { items: TaskDto[] } | undefined;
    const ex = data?.items.find((x) => x.id === t.id);
    if (!ex || ex.version >= t.version) continue;
    patchLive<{ items: TaskDto[] }>(q.queryKey, (old) => (old ? { ...old, items: old.items.map((x) => (x.id === t.id && x.version < t.version ? t : x)) } : old));
  }
  // Unteraufgaben stehen nicht in der Liste, sondern bei ihrer Aufgabe (Paket P8)
  if (t.parentId) {
    queryClient.setQueryData<{ items: TaskDto[] }>(keys.subtasks(pid, t.parentId), (old) => {
      if (!old) return old;
      const ex = old.items.find((x) => x.id === t.id);
      if (ex && ex.version > t.version) return old;
      return { items: [...old.items.filter((x) => x.id !== t.id), t].sort(byPos) };
    });
    return;
  }
  for (const state of ['open', 'done'] as const) {
    queryClient.setQueryData<TaskPages>(keys.tasks(pid, state), (old) => {
      if (!old) return old;
      const existing = old.pages.flatMap((p) => p.items).find((x) => x.id === t.id);
      if (existing && existing.version > t.version) return old;
      const belongs = state === 'open' ? !t.completedAt : !!t.completedAt;
      const pages = old.pages.map((p) => ({ ...p, items: p.items.filter((x) => x.id !== t.id) }));
      if (!belongs) return { ...old, pages };
      // In die Seite einsortieren, deren Bereich die Position abdeckt (sonst ans Ende der geladenen Daten)
      const all = pages.flatMap((p) => p.items);
      all.push(t);
      all.sort(compareTasks);
      let i = 0;
      const sized = pages.map((p, idx) => {
        const n = p.items.length + (idx === pages.length - 1 ? 1 : 0);
        const items = all.slice(i, i + n);
        i += n;
        return { ...p, items };
      });
      return { ...old, pages: sized };
    });
  }
}

/**
 * Reihenfolge wie auf dem Server: Liste, dann Position (Byte-Vergleich wie COLLATE "C"), dann ID. Feldweise
 * vergleichen – eine verkettete Zeichenkette sortiert z. B. „a1V“ fälschlich vor „a1“.
 */
export function compareTasks(a: TaskDto, b: TaskDto): number {
  if (a.listId !== b.listId) return a.listId < b.listId ? -1 : 1;
  if (a.position !== b.position) return a.position < b.position ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function removeTask(pid: string, id: string) {
  queryClient.removeQueries({ queryKey: keys.task(pid, id), exact: true });
  queryClient.setQueriesData<{ items: TaskDto[] }>({ queryKey: ['p', pid, 'subtasks'] }, (old) => (old?.items.some((x) => x.id === id) ? { items: old.items.filter((x) => x.id !== id) } : old));
  for (const state of ['open', 'done'] as const) {
    queryClient.setQueryData<TaskPages>(keys.tasks(pid, state), (old) =>
      old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.filter((x) => x.id !== id) })) } : old,
    );
  }
}

export const realtime = new Realtime();

export function useConnectionStatus(): Status {
  return useSyncExternalStore(realtime.subscribeStatus, realtime.getStatus);
}

/** Projektstrom abonnieren, sobald der Projekt-Snapshot mit Cursor vorliegt */
export function useProjectLive(projectId: string, cursor: number | undefined) {
  useEffect(() => {
    if (cursor === undefined) return;
    return realtime.subscribe({ type: 'project', id: projectId }, cursor);
  }, [projectId, cursor === undefined]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Workspace-Strom abonnieren, sobald ein Snapshot mit Cursor vorliegt */
export function useWorkspaceLive(workspaceId: string, cursor: number | undefined) {
  useEffect(() => {
    if (cursor === undefined) return;
    return realtime.subscribe({ type: 'workspace', id: workspaceId }, cursor);
  }, [workspaceId, cursor === undefined]); // eslint-disable-line react-hooks/exhaustive-deps
}
